/**
 * Consented localStorage helpers for parent-list table query state.
 *
 * Keys are built from a client-scoped table `storageKey` plus a suffix:
 * - `-filters` JSON `{ filters, search }`
 * - `-filter-visibility` JSON string[] of enabled funnel chip IDs
 * - `-current-page` 1-based page index
 * - `-sorting` JSON SortingState
 */

import { getConsentedStorage } from "@/lib/utils/consented-storage";
import { isActiveFilterValue } from "@/lib/utils/filter-utils";
import type { SortingState } from "@tanstack/react-table";

export const TABLE_FILTERS_SUFFIX = "-filters";
export const TABLE_FILTER_VISIBILITY_SUFFIX = "-filter-visibility";
export const TABLE_CURRENT_PAGE_SUFFIX = "-current-page";
export const TABLE_SORTING_SUFFIX = "-sorting";

export type TableFilterValue = string | string[] | { from: string; to: string };
export type TableFilters = Record<string, TableFilterValue>;

export interface TableToolbarState {
  filters: TableFilters;
  search: string;
}

const EMPTY_TOOLBAR: TableToolbarState = { filters: {}, search: "" };

function readRaw(key: string): string | null {
  try {
    if (globalThis.window === undefined) return null;
    return getConsentedStorage().getItem(key);
  } catch {
    return null;
  }
}

function writeRaw(key: string, value: string): void {
  try {
    if (globalThis.window === undefined) return;
    getConsentedStorage().setItem(key, value);
  } catch {
    // Quota / access errors — ignore
  }
}

function readJson<T>(key: string): T | null {
  const raw = readRaw(key);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

function writeJson(key: string, value: unknown): void {
  writeRaw(key, JSON.stringify(value));
}

function isFilterValue(value: unknown): value is TableFilterValue {
  if (typeof value === "string") return true;
  if (Array.isArray(value)) {
    return value.every((item) => typeof item === "string");
  }
  if (typeof value === "object" && value !== null) {
    const record = value as Record<string, unknown>;
    return typeof record.from === "string" && typeof record.to === "string";
  }
  return false;
}

function parseToolbarState(value: unknown): TableToolbarState | null {
  if (typeof value !== "object" || value === null) return null;
  const record = value as Record<string, unknown>;
  const search = typeof record.search === "string" ? record.search : "";
  const filtersRaw = record.filters;
  if (typeof filtersRaw !== "object" || filtersRaw === null) {
    return { filters: {}, search };
  }
  const filters: TableFilters = {};
  for (const [key, filterValue] of Object.entries(filtersRaw)) {
    if (isFilterValue(filterValue)) {
      filters[key] = filterValue;
    }
  }
  return { filters, search };
}

function parseSorting(value: unknown): SortingState | null {
  if (!Array.isArray(value)) return null;
  const sorting: SortingState = [];
  for (const item of value) {
    if (
      typeof item === "object" &&
      item !== null &&
      typeof (item as { id?: unknown }).id === "string" &&
      typeof (item as { desc?: unknown }).desc === "boolean"
    ) {
      sorting.push({
        id: (item as { id: string }).id,
        desc: (item as { desc: boolean }).desc,
      });
    }
  }
  return sorting;
}

export function readToolbarState(storageKey: string): TableToolbarState | null {
  if (!storageKey) return null;
  const parsed = parseToolbarState(
    readJson<unknown>(`${storageKey}${TABLE_FILTERS_SUFFIX}`),
  );
  return parsed;
}

export function writeToolbarState(
  storageKey: string,
  state: TableToolbarState,
): void {
  if (!storageKey) return;
  writeJson(`${storageKey}${TABLE_FILTERS_SUFFIX}`, state);
}

function parseFilterVisibility(value: unknown): string[] | null {
  if (!Array.isArray(value)) return null;
  const ids: string[] = [];
  for (const item of value) {
    if (typeof item !== "string") return null;
    ids.push(item);
  }
  return ids;
}

/** Revision 1 keeps `{key}-filter-visibility`. Later revisions use `{key}-vN-filter-visibility`. */
export function filterVisibilityStorageKey(
  storageKey: string,
  revision = 1,
): string {
  if (revision > 1) {
    return `${storageKey}-v${revision}${TABLE_FILTER_VISIBILITY_SUFFIX}`;
  }
  return `${storageKey}${TABLE_FILTER_VISIBILITY_SUFFIX}`;
}

export function readFilterVisibility(
  storageKey: string,
  revision = 1,
): string[] | null {
  if (!storageKey) return null;
  return parseFilterVisibility(
    readJson<unknown>(filterVisibilityStorageKey(storageKey, revision)),
  );
}

export function writeFilterVisibility(
  storageKey: string,
  enabledIds: readonly string[],
  revision = 1,
): void {
  if (!storageKey) return;
  writeJson(filterVisibilityStorageKey(storageKey, revision), [...enabledIds]);
}

export function readCurrentPage(storageKey: string): number | null {
  if (!storageKey) return null;
  const raw = readRaw(`${storageKey}${TABLE_CURRENT_PAGE_SUFFIX}`);
  if (!raw) return null;
  const parsed = Number(raw);
  if (!Number.isInteger(parsed) || parsed < 1) return null;
  return parsed;
}

export function writeCurrentPage(storageKey: string, page: number): void {
  if (!storageKey || page < 1) return;
  writeRaw(`${storageKey}${TABLE_CURRENT_PAGE_SUFFIX}`, String(page));
}

export function readSorting(storageKey: string): SortingState | null {
  if (!storageKey) return null;
  return parseSorting(
    readJson<unknown>(`${storageKey}${TABLE_SORTING_SUFFIX}`),
  );
}

export function writeSorting(storageKey: string, sorting: SortingState): void {
  if (!storageKey) return;
  writeJson(`${storageKey}${TABLE_SORTING_SUFFIX}`, sorting);
}

export function hasActiveToolbarState(state: TableToolbarState): boolean {
  if (state.search.trim() !== "") return true;
  return Object.values(state.filters).some((value) =>
    isActiveFilterValue(value),
  );
}

export function emptyToolbarState(): TableToolbarState {
  return { filters: { ...EMPTY_TOOLBAR.filters }, search: "" };
}
